import React, { useState, useEffect } from 'react';
import { Box, Paper, useTheme, alpha, Skeleton, Tooltip } from '@mui/material';
import { Checkbox, DatePicker, IconButton, Input, Typography, Dialog } from '@sm-dashboard/ui-components';
import type { Theme } from '@mui/material/styles';
import ChecklistIcon from '@mui/icons-material/Checklist';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { getJewishDay } from 'jewish-dates-core';
import dayjs, { type Dayjs } from 'dayjs';
import { useFetch } from '@/hooks/useFetch';
import { tasksUrl, createTaskUrl, updateTaskUrl, deleteTaskUrl } from '@/constants';
import instanceAxios from '@/util/axios';
import DynamicDialog from '@/components/dashboard/DaynmicDialog';
import { motion } from 'framer-motion';
import { useEventAnimation } from '@/hooks/useEventAnimation';

interface Task {
  id: number;
  title: string;
  dueDate?: string | Date;
  description?: string;
  completed: boolean;
  createdAt: string | Date;
  updatedAt: string | Date;
}

interface TaskItemProps {
  task: Task;
  onToggleCompleted: (task: Task) => Promise<void>;
  onEdit: () => void;
  onDelete: () => void;
  formatTaskDate: (date?: string | Date) => string;
  isDueDatePassed: (dueDate?: string | Date) => boolean;
  theme: Theme;
}

const taskVariants = {
  active: {
    opacity: 1,
    filter: 'grayscale(0%)',
    transition: { duration: 0.5, ease: 'easeInOut' }
  },
  inactive: {
    opacity: 0.6,
    filter: 'grayscale(100%)',
    transition: { duration: 0.5, ease: 'easeInOut' }
  },
  moving: {
    zIndex: 1000,
    transition: { duration: 0.3 }
  }
};

const TaskItem: React.FC<TaskItemProps> = ({
  task,
  onToggleCompleted,
  onEdit,
  onDelete,
  formatTaskDate,
  isDueDatePassed,
  theme
}) => {
  const { isTransitioning, startTransition } = useEventAnimation();

  const handleToggleTask = async () => {
    await startTransition(async () => {
      await onToggleCompleted(task);
    });
  };

  return (
    <motion.div
      variants={taskVariants}
      animate={isTransitioning ? 'moving' : !task.completed ? 'active' : 'inactive'}
      layout
      layoutId={`task-${task.id}`}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          p: { xs: 1.5, sm: 2 },
          borderRadius: 1,
          bgcolor: 'common.white',
          minHeight: { xs: 'auto', sm: 100 },
          opacity: task.completed ? 0.7 : 1,
          '&:hover': {
            bgcolor: alpha(theme.palette.grey[50], 0.8)
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                flexWrap: 'wrap'
              }}
            >
              <Checkbox
                checked={task.completed}
                onChange={handleToggleTask}
                size="small"
                density="compact"
                disabled={isTransitioning}
              />
              <Box
                sx={{
                  textDecoration: task.completed ? 'line-through' : 'none',
                  wordBreak: 'break-word'
                }}
              >
                <Typography variant="subtitle2" color="text.primary">
                  {task.title}
                </Typography>
              </Box>
              {!task.completed && task.dueDate && isDueDatePassed(task.dueDate) && (
                <Tooltip title="הזמן למשימה עבר">
                  <WarningIcon sx={{ fontSize: 18, color: 'error.main' }} />
                </Tooltip>
              )}
            </Box>
            <Typography variant="body" color="text.secondary">
              {formatTaskDate(task.createdAt)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <IconButton size="small" color="primary" aria-label="עריכת משימה" onClick={onEdit}>
              <EditIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
            <IconButton size="small" color="warning" aria-label="מחיקת משימה" onClick={onDelete}>
              <DeleteOutlineIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />
            </IconButton>
          </Box>
        </Box>
        {task.description && (
          <Typography variant="body" color="text.secondary">
            {task.description}
          </Typography>
        )}
      </Box>
    </motion.div>
  );
};

const TasksCard: React.FC = () => {
  const theme = useTheme();
  const { data: tasksData, loading, fetchData: fetchTasks } = useFetch();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    dueDate: '',
    completed: false
  });

  const isDueDatePassed = (dueDate?: string | Date): boolean => {
    if (!dueDate) return false;
    try {
      const dueDateObj = typeof dueDate === 'string' ? new Date(dueDate) : dueDate;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dueDateObj.setHours(0, 0, 0, 0);
      return dueDateObj < today;
    } catch (e) {
      return false;
    }
  };

  useEffect(() => {
    fetchTasks({
      method: 'GET',
      url: `${tasksUrl}?page=0&rowsPerPage=1000`
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tasksData?.rows) {
      const sortedTasks = [...tasksData.rows].sort((a, b) => {
        if (a.completed && !b.completed) return 1;
        if (!a.completed && b.completed) return -1;
        const aDueDatePassed = isDueDatePassed(a.dueDate);
        const bDueDatePassed = isDueDatePassed(b.dueDate);
        if (aDueDatePassed && !bDueDatePassed) return -1;
        if (!aDueDatePassed && bDueDatePassed) return 1;
        return 0;
      });
      setTasks(sortedTasks);
    }
  }, [tasksData]);

  const handleDeleteTask = (task: Task) => {
    setTaskToDelete(task);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;
    try {
      await instanceAxios.delete(`${deleteTaskUrl}?id=${taskToDelete.id}`);
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks({
        method: 'GET',
        url: `${tasksUrl}?page=0&rowsPerPage=1000`
      });
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleOpenDialog = (task?: Task) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || '',
        dueDate: task.dueDate
          ? typeof task.dueDate === 'string'
            ? task.dueDate.split('T')[0]
            : dayjs(task.dueDate).format('YYYY-MM-DD')
          : '',
        completed: false
      });
    } else {
      setEditingTask(null);
      setFormData({
        title: '',
        description: '',
        dueDate: '',
        completed: false
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
    setFormData({
      title: '',
      description: '',
      dueDate: '',
      completed: false
    });
  };

  const handleSaveTask = async () => {
    try {
      const taskData = {
        title: formData.title,
        description: formData.description || null,
        dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : null,
        completed: false
      };

      if (editingTask) {
        await instanceAxios.post(updateTaskUrl, {
          id: editingTask.id,
          ...taskData
        });
      } else {
        await instanceAxios.post(createTaskUrl, taskData);
      }

      handleCloseDialog();
      fetchTasks({
        method: 'GET',
        url: `${tasksUrl}?page=0&rowsPerPage=1000`
      });
    } catch (error) {
      console.error('Error saving task:', error);
    }
  };

  const handleToggleCompleted = async (task: Task) => {
    try {
      await instanceAxios.post(updateTaskUrl, {
        id: task.id,
        completed: !task.completed
      });
      fetchTasks({
        method: 'GET',
        url: `${tasksUrl}?page=0&rowsPerPage=1000`
      });
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const formatTaskDate = (date?: string | Date): string => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      const gregorianDate = dateObj.toLocaleDateString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });

      let hebrewDate = '';
      try {
        const jewishDay = getJewishDay(dayjs(dateObj) as Dayjs);
        if (jewishDay?.jewishDateStrHebrew) {
          hebrewDate = jewishDay.jewishDateStrHebrew;
        }
      } catch (e) {
        // ignore error
      }

      return hebrewDate ? `${hebrewDate} ${gregorianDate}` : gregorianDate;
    } catch (e) {
      return typeof date === 'string' ? date : date.toLocaleDateString('he-IL');
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          bgcolor: 'info.main',
          color: 'common.white',
          p: 1,
          pl: 2,
          borderRadius: '30px 30px 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexDirection: 'row',
          flexShrink: 0
        }}
      >
        <Box
          sx={{
            width: 12,
            height: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <ChecklistIcon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ flex: 1, textAlign: 'left' }}>
          <Typography variant="h6">לוח משימות</Typography>
        </Box>
      </Box>

      <Paper
        sx={{
          borderRadius: '0 0 20px 20px',
          overflow: 'hidden',
          bgcolor: alpha(theme.palette.grey[300], 0.5),
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 280, sm: 400 },
          height: { xs: 'auto', sm: 400 },
          maxHeight: { xs: 400, sm: 'none' },
          p: { xs: 1, sm: 2 }
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1
          }}
        >
          {loading ? (
            <>
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
              <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1 }} />
            </>
          ) : tasks.length === 0 ? (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                py: 4
              }}
            >
              <Box sx={{ color: 'text.secondary' }}>
                <Typography variant="body">אין משימות להצגה</Typography>
              </Box>
            </Box>
          ) : (
            tasks.map(task => (
              <TaskItem
                key={task.id}
                task={task}
                onToggleCompleted={handleToggleCompleted}
                onEdit={() => handleOpenDialog(task)}
                onDelete={() => handleDeleteTask(task)}
                formatTaskDate={formatTaskDate}
                isDueDatePassed={isDueDatePassed}
                theme={theme}
              />
            ))
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            pt: 1
          }}
        >
          <IconButton
            size="small"
            color="info"
            shape="circle"
            aria-label="הוספת משימה"
            onClick={() => handleOpenDialog()}
          >
            <AddIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        title={editingTask ? 'עריכת משימה' : 'הוספת משימה חדשה'}
        cancelAction
        primaryAction={{ label: 'שמור', onClick: handleSaveTask, disabled: !formData.title.trim() }}
      >
        <Box
          sx={{
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.palette.grey[400], 0.5),
              borderRadius: '4px'
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2, position: 'relative' }}>
            <Input
              appearance="plain"
              label="כותרת המשימה"
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              fullWidth
              required
              autoFocus
            />
            <Input
              appearance="plain"
              label="פרטי המשימה"
              placeholder="הכנס את פרטי המשימה"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              multiline
              rows={5}
            />
            <DatePicker
              appearance="plain"
              label="תאריך יעד (אופציונלי)"
              value={formData.dueDate || ''}
              onChange={value => setFormData({ ...formData, dueDate: value || '' })}
            />
          </Box>
        </Box>
      </Dialog>

      <DynamicDialog
        open={deleteDialogOpen}
        title="מחיקת משימה"
        subtitle={`האם אתה בטוח שברצונך למחוק את המשימה ${taskToDelete?.title}? פעולה זו אינה ניתנת לביטול.`}
        handleOkClose={handleConfirmDelete}
        handleNotOkClose={() => {
          setDeleteDialogOpen(false);
          setTaskToDelete(null);
        }}
      />
    </Box>
  );
};

export default TasksCard;
